import { axiosInstance } from "../services/v2/axiosInstance";

/** Centro de Avisos (Esteban, 2026-10-10): bandeja, Mis Avisos y Configurar Avisos. */
export type Canal = "wapp" | "push" | "wappteam" | "correo";
export type Prioridad = "urgente" | "normal" | "informativo";

export interface Aviso {
	id: number; notificar?: boolean; titulo: string; cuerpo: string | null; enlace: string | null; plantel: string | null; prioridad: Prioridad; leido: boolean;
	creado: string; tipo: string; code: string; grupo: string; canales: Record<string, string>;
}
export interface Bandeja { avisos: Aviso[]; hay_mas: boolean; no_leidos: number; por_grupo: Record<string, number>; }

export interface TipoPref {
	id: number; code: string; nombre: string; descripcion: string | null; grupo: string; prioridad: Prioridad; permitidos: Canal[]; bandeja: boolean;
	canales: Canal[]; modo: "inmediato" | "resumen"; apagado: boolean; personal: boolean; defecto: { canales: Canal[]; modo: string };
	te_llega: "siempre" | "cuando_participas" | "no";
}
export interface Preferencias {
	tipos: TipoPref[]; general: { resumen: string[]; silencio: { desde: string; hasta: string } | null; zona: string };
	grupos: string[]; canales: Record<Canal, string>;
}

export interface TipoCatalogo {
	id: number; code: string; nombre: string; grupo: string; prioridad: Prioridad; canales_defecto: Canal[]; modo_defecto: string; activo: boolean;
	configurable: boolean; bandeja: boolean; inventario: number | null; reglas: number; semana: number; fallas: number;
}
export interface Catalogo {
	tipos: TipoCatalogo[]; grupos: string[]; canales: Record<Canal, string>;
	roles: { id: number; nombre: string }[]; planteles: { id: number; nombre: string }[];
}
export interface Regla {
	id: number; clase: "rol" | "relacion" | "usuario" | "correo"; role_id: number | null; alcance: string | null; relacion: string | null;
	user_id: number | null; email: string | null; campus_id: number | null; pais: string | null; excluir: boolean; nota: string | null; texto: string;
}
export interface DetalleTipo {
	tipo: {
		id: number; code: string; nombre: string; descripcion: string | null; grupo: string; prioridad: Prioridad; canales_permitidos: Canal[];
		canales_defecto: Canal[]; modo_defecto: string; intervalo_min: number | null; bandeja: boolean; configurable: boolean; activo: boolean;
		inventario: number | null; origen: string | null;
	};
	relaciones: { code: string; nombre: string }[]; reglas: Regla[];
	envios: { id: number; canal: string; estado: string; detalle: string | null; creado: string; quien: string | null }[];
}
export interface PersonaVista { id: number; nombre: string; correo: string | null; razones: string[]; roles?: string[]; canales?: Canal[]; modo?: string; apagado?: boolean; }
export interface VistaPrevia { incluidos: PersonaVista[]; excluidos: PersonaVista[]; correos: string[]; ademas: string[]; }

export const avisosApi = {
	bandeja: (f: { grupo?: string; no_leidos?: boolean; antes?: number; desde?: number; limite?: number }) =>
		axiosInstance.get<Bandeja>("/avisos", { params: { ...f, no_leidos: f.no_leidos ? 1 : undefined } }),
	conteo: () => axiosInstance.get<{ no_leidos: number }>("/avisos/_conteo"),
	leer: (d: { ids?: number[]; todos?: boolean; grupo?: string; leido?: boolean }) => axiosInstance.post<{ no_leidos: number }>("/avisos/_leer", d),
	preferencias: () => axiosInstance.get<Preferencias>("/avisos/_preferencias"),
	guardarPreferencia: (d: { tipo_id: number; canales?: Canal[]; modo?: string; apagado?: boolean; restablecer?: boolean }) =>
		axiosInstance.put<Preferencias>("/avisos/_preferencias", d),
	guardarGeneral: (d: { resumen: string[]; silencio: { desde: string; hasta: string } | null }) => axiosInstance.put<Preferencias>("/avisos/_preferencias/_general", d),
	catalogo: () => axiosInstance.get<Catalogo>("/avisos/config/_catalogo"),
	tipo: (id: number) => axiosInstance.get<DetalleTipo>(`/avisos/config/tipos/${id}`),
	guardarTipo: (id: number, d: any) => axiosInstance.put<DetalleTipo>(`/avisos/config/tipos/${id}`, d),
	agregarRegla: (id: number, d: any) => axiosInstance.post<DetalleTipo>(`/avisos/config/tipos/${id}/reglas`, d),
	quitarRegla: (reglaId: number) => axiosInstance.delete<DetalleTipo>(`/avisos/config/reglas/${reglaId}`),
	vista: (id: number, campusId: number | null, pais: string | null) =>
		axiosInstance.get<VistaPrevia>(`/avisos/config/tipos/${id}/_vista`, { params: { campus_id: campusId || undefined, pais: pais || undefined } }),
	personas: (q: string) => axiosInstance.get<{ id: number; nombre: string; correo: string }[]>("/avisos/config/_personas", { params: { q } }),
	prueba: (id: number) => axiosInstance.post<{ resultado: any }>(`/avisos/config/tipos/${id}/_prueba`),
};

export const errorTexto = (e: any): string => e?.response?.data?.message || e?.message || "Algo falló. Intenta otra vez.";

export const NOMBRE_CANAL: Record<Canal, string> = { wapp: "WAPP", push: "Celular", wappteam: "WappTeam", correo: "Correo" };

/** Abre el enlace del aviso dentro de WAPP si es de aquí; si no, en otra pestaña. */
export const abrirEnlace = (enlace: string | null, navigate: (r: string) => void) => {
	if (!enlace) return;
	if (enlace.startsWith("/")) { navigate(enlace); return; }
	try {
		const u = new URL(enlace);
		if (u.origin === window.location.origin) { navigate(u.pathname + u.search); return; }
	} catch { /* no es URL */ }
	window.open(enlace, "_blank", "noopener");
};

/** "hace 5 min", "ayer 4:10 pm", "12 oct". */
export const cuando = (iso: string): string => {
	const d = new Date(iso);
	const s = Math.max(0, (Date.now() - d.getTime()) / 1000);
	if (s < 60) return "ahora";
	if (s < 3600) return "hace " + Math.floor(s / 60) + " min";
	const hora = d.toLocaleTimeString("es-MX", { hour: "numeric", minute: "2-digit", hour12: true }).replace(/\s?a\.\s?m\./i, " am").replace(/\s?p\.\s?m\./i, " pm");
	const hoy = new Date();
	if (d.toDateString() === hoy.toDateString()) return hora;
	const ayer = new Date(hoy.getTime() - 86400000);
	if (d.toDateString() === ayer.toDateString()) return "ayer " + hora;
	return d.toLocaleDateString("es-MX", { day: "numeric", month: "short" }).replace(".", "");
};

/** "17:00" → "5:00 pm". */
export const horaAmPm = (h: string): string => {
	const [hh, mm] = h.split(":").map(Number);
	if (isNaN(hh)) return h;
	return ((hh % 12) || 12) + ":" + String(mm || 0).padStart(2, "0") + (hh < 12 ? " am" : " pm");
};
